// FILE: CostBreakdown.jsx (REVISED - Corrected Logic & Data Handling)

import React from 'react';
import { FaRegFileAlt, FaShieldAlt, FaSearchDollar, FaCheckCircle, FaTimesCircle, FaInfoCircle, FaDollarSign, FaLightbulb, FaBuilding, FaLandmark } from 'react-icons/fa'; // Added FaLandmark, FaBuilding
import { GiScales } from 'react-icons/gi';

// Helper to safely get and format currency
const formatCurrencyDisplay = (value, defaultValue = '$0') => {
    const num = Number(value);
    if (isNaN(num)) {
        return defaultValue;
    }
    return `$${num.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

// Helper to get nested values safely
const getValue = (obj, path, defaultValue = null) => {
    try {
        const value = path.split('.').reduce((o, k) => (o || {})[k], obj);
        return value !== undefined && value !== null ? value : defaultValue;
    } catch (e) {
        return defaultValue;
    }
};


const CostBreakdown = ({
    type,
    // Expecting the FULL 'calculations' object for Independent type,
    // or 'calculations.fiscalSponsorship' object for FS type
    costs = {},
    state = null, // Primary state code (e.g., 'TX')
    // Expecting the flags object: { initialRegistration: bool, annualRenewal: bool, ... }
    // Directly access from costs.stateSpecific.requirements for Independent
    // Not needed directly for FS
    revenue = 0, // Passed down for context if needed
    validationInfo = {} // Retained if used elsewhere
}) => {
    const isFiscalSponsorship = type === "Fiscal Sponsorship";

    // --- Data Extraction and Formatting ---
    let displayItems = []; // Array to hold items for rendering
    let totalCostToDisplay = 0;
    let overheadPercentageDisplay = '';
    let firstYearLabel = 'Annual Cost'; // Default for FS
    let stateRequirementsFlags = null; // For Independent state reqs display

    if (isFiscalSponsorship) {
        // For FS, 'costs' is calculations.fiscalSponsorship
        totalCostToDisplay = getValue(costs, 'totalAnnualFee', 0);
        overheadPercentageDisplay = `${getValue(costs, 'percentage', 5)}%`; // Use passed percentage or default

        displayItems.push({
            key: "Admin Fee",
            label: `Administrative Fee (${overheadPercentageDisplay} of Revenue)`,
            value: getValue(costs, 'totalAnnualFee', 0),
            tooltip: "Covers comprehensive nonprofit infrastructure: compliance, financial management, tax filings, insurance, HR support, governance, and operational support provided through the fiscal sponsorship program.",
            isTotal: false,
            isBold: false,
        });

    } else {
        // For Independent, 'costs' is the full 'calculations' object.
        totalCostToDisplay = getValue(costs, 'totalFirstYear', 0); // Show First Year cost as main figure
        firstYearLabel = 'Total First Year';
        const adminPct = getValue(costs, 'annual.administrativeOverheadPercentage.percentage', null);
        overheadPercentageDisplay = adminPct !== null ? `${adminPct.toFixed(1)}%*` : 'N/A';
        stateRequirementsFlags = getValue(costs, 'stateSpecific.requirements', {}); // Get flags for rendering

        // Build the displayItems array reflecting the methodology

        // --- Startup Costs ---
        displayItems.push({
            key: "StartupTotal",
            label: "Total Startup Costs (One-Time)",
            value: getValue(costs, 'startup.total', 0),
            tooltip: `Includes: Form 1023 (${formatCurrencyDisplay(getValue(costs, 'startup.form1023.cost'))}), Legal Setup (${formatCurrencyDisplay(getValue(costs, 'startup.legalSetup.cost'))}), State Incorp. Fee (${formatCurrencyDisplay(getValue(costs, 'startup.initialIncorporationFee.cost'))}), Initial Charity Reg. (${formatCurrencyDisplay(getValue(costs, 'startup.stateRegistrations.initialCost'))}).`,
            isTotal: true, // Mark as a sub-total row
            isBold: true,
        });

        // --- Annual Costs ---
         displayItems.push({
             key: "AnnualHeader", // Placeholder for visual separation if needed in CSS
             label: "Annual Ongoing Costs:",
             value: null, // No value, just a header label
             tooltip: null,
             isHeader: true, // Flag for styling
             isBold: true,
         });

        displayItems.push({
            key: "CoreAdmin",
            label: "Core Admin Overhead",
            value: getValue(costs, 'annual.administrativeOverhead.cost', 0),
            tooltip: getValue(costs, 'annual.administrativeOverhead.tooltip', 'Core operational costs (staff, accounting, rent, utilities, etc.) based on validated overhead rate.'),
            isTotal: false,
            isBold: false,
        });
        // Only add discrete costs if they are > 0
        const auditCost = getValue(costs, 'annual.audit.cost', 0);
        if (auditCost > 0) {
            displayItems.push({
                key: "Audit", label: "Audit / Review", value: auditCost,
                tooltip: getValue(costs, 'annual.audit.tooltip', 'Required financial oversight costs (if applicable).'),
                isTotal: false, isBold: false,
            });
        }
        const stateRenewalCost = getValue(costs, 'annual.stateRenewals.annualCost', 0);
         if (stateRenewalCost > 0) {
            displayItems.push({
                key: "StateRenewals", label: "State Renewals & Reg. Agent", value: stateRenewalCost,
                tooltip: getValue(costs, 'annual.stateRenewals.tooltip', 'Annual state compliance service fees (renewals, registered agent).'),
                isTotal: false, isBold: false,
            });
        }
        const boardMgmtCost = getValue(costs, 'annual.boardManagement.cost', 0);
        if (boardMgmtCost > 0) {
            displayItems.push({
                key: "BoardMgmt", label: "Board Management", value: boardMgmtCost,
                tooltip: getValue(costs, 'annual.boardManagement.tooltip', 'Specific governance support costs.'),
                isTotal: false, isBold: false,
            });
        }
        const complianceCost = getValue(costs, 'annual.complianceAndInsurance.cost', 0);
        if (complianceCost > 0) {
            displayItems.push({
                key: "Compliance", label: "Compliance & Insurance", value: complianceCost,
                tooltip: getValue(costs, 'annual.complianceAndInsurance.tooltip', 'Specific risk management policies & monitoring.'),
                isTotal: false, isBold: false,
            });
        }

         // --- Total Annual Ongoing ---
         displayItems.push({
             key: "AnnualTotal",
             label: "Total Annual Ongoing Costs",
             value: getValue(costs, 'annual.total', 0), // Use the calculated annual total
             tooltip: 'Sum of Core Admin Overhead and other discrete annual costs listed above.',
             isTotal: true,
             isBold: true,
         });

        // NOTE: Form 990 prep cost (costs.annual.form990.cost) is intentionally NOT displayed here
        // as it's assumed to be covered within the Core Admin Overhead value for methodology accuracy.
    }


    // --- Static Data (If used - check if these are still needed/accurate) ---
    const coreBenefitsFS = [
      "501(c)(3) Status Coverage", "Financial Management & Reporting", "Payroll & HR Administration",
      "Donation Processing", "Grant Management Support", "Basic Insurance Coverage (GL, D&O)",
      "Compliance Monitoring (State/Federal)", "Audit Coordination (if needed)"
    ];
    const additionalServicesFS = [
        "Strategic Planning Guidance", "Fundraising Support", "Marketing & Communications Assistance",
        "Technology Platform Access", "Board Development Resources"
    ];
    // Independent Responsibilities (Examples - can be dynamically generated based on cost items)
    const independentResponsibilities = [
        { name: "IRS Filings (Form 1023, 990)", cost: "Varies ($$ - $$$$)" , source:"IRS Fees + CPA/Legal Prep"},
        { name: "State Charity Registration & Renewals", cost: "Varies by state ($ - $$$)" , source:"State Fees + Filing Service"},
        { name: "State Corporate Filings", cost: "Varies by state ($ - $$)" , source:"State Fees + Registered Agent"},
        { name: "Audit / Financial Review", cost: "$$$$ - $$$$$", source:"CPA Firm Fees (If required)" },
        { name: "Bookkeeping & Accounting", cost: "$$ - $$$$ / mo", source:"Staff or Service" },
        { name: "Payroll & HR Admin", cost: "$$ - $$$ / mo", source:"Software or Service" },
        { name: "Insurance Policies (GL, D&O, WC)", cost: "$$ - $$$$ / yr", source:"Insurance Brokers" },
        { name: "Board Management & Governance", cost: "$ - $$ / mo", source:"Software, Staff Time" }
    ];


    // --- Render Functions ---
    const renderStateRequirements = () => {
        if (!stateRequirementsFlags) return null; // Only render for Independent type with flags

        const reqs = stateRequirementsFlags;
        const auditReq = getValue(reqs, 'auditRequired', false);
        const reviewReq = getValue(reqs, 'financialReviewRequired', false) && !auditReq; // Only relevant if audit not required

        const renderReqItem = (label, isRequired, iconClassRequired = "green", iconClassNotRequired = "red") => (
            <div className="requirement-item">
                <span>{label}:</span>
                <span className="status">
                    {isRequired ? <FaCheckCircle className={`icon-required ${iconClassRequired}`} /> : <FaTimesCircle className={`icon-not-required ${iconClassNotRequired}`} />}
                    {isRequired ? "Likely Required" : "Likely Not Required"}
                </span>
            </div>
        );

        return (
            <div className="requirements-grid">
                <div className="requirement-card">
                    <h4 className="req-title"><FaLandmark /> State Filing/Registration</h4>
                    {renderReqItem("Initial Charity Reg", getValue(reqs, 'initialRegistration', false))}
                    {renderReqItem("Annual Charity Renewal", getValue(reqs, 'annualRenewal', false))}
                    {renderReqItem("Sec. of State Filing", getValue(reqs, 'secretaryOfStateRequired', true))} {/* Assume true if not specified */}
                    {renderReqItem("Registered Agent", getValue(reqs, 'registeredAgentRequired', false))}
                </div>
                <div className="requirement-card">
                     <h4 className="req-title"><GiScales /> Financial Oversight</h4>
                    {renderReqItem("Annual Audit", auditReq)}
                    {renderReqItem("Financial Review", reviewReq)}
                     <div className="requirement-note">
                       <FaInfoCircle /> Triggered by state/federal revenue/funding thresholds. Costs reflected above if applicable.
                     </div>
                </div>
            </div>
        );
    };

    const renderIndependentResponsibilitiesList = () => {
        // Could potentially map actual cost items from 'costs' object here for more dynamic display
        return independentResponsibilities.map((req, index) => (
             <li key={index} className="requirement-item independent-req">
                 <div className="icon-text-row">
                     <FaSearchDollar className="required-icon orange" /> {/* Use a neutral/warning color */}
                     <span>{req.name}</span>
                 </div>
                 <div className="cost-tooltip-row">
                    <span className="requirement-cost">{req.cost}</span>
                    {req.source && (
                        <div className="info-tooltip">
                           <FaInfoCircle className="info-icon-inline tiny" />
                           <div className="tooltip-content-inline source-citation">{req.source}</div>
                        </div>
                    )}
                </div>
            </li>
        ));
    };

    // --- Main Return ---
    return (
        <div className={`comparison-card ${isFiscalSponsorship ? 'fiscal-sponsorship' : 'independent'}`}>
            <h2>{type}</h2>

            {/* Total Cost Display */}
            <div className="cost-display">
                <span className={`amount ${isFiscalSponsorship ? 'affordable' : 'expensive'}`}>
                    {formatCurrencyDisplay(totalCostToDisplay)}
                </span>
                <span className="period">{firstYearLabel}</span>
                 {/* Show ongoing cost clearly for Independent */}
                 {!isFiscalSponsorship && (
                    <span className="period ongoing">
                        (Ongoing: {formatCurrencyDisplay(getValue(costs, 'totalOngoing', 0))} / year)
                    </span>
                 )}
            </div>

            {/* Admin Overhead Rate Display */}
             <div className="overhead-rate-display">
                 <span className="label">Est. Admin Overhead Rate:</span>
                 <span className={`value ${isFiscalSponsorship ? 'fs' : 'ind'}`}>{overheadPercentageDisplay}</span>
                 {/* Tooltip for Independent Overhead Breakdown */}
                 {!isFiscalSponsorship && getValue(costs, 'annual.administrativeOverheadPercentage.percentage', null) !== null && (
                     <div className="info-tooltip-inline">
                         <FaInfoCircle className="info-icon-inline" />
                         <div className="tooltip-content-inline overhead-breakdown">
                             {`*Based on:
                              Base: ${getValue(costs, 'annual.administrativeOverheadPercentage.basePercentage', 0).toFixed(1)}% +
                              Complexity: ${getValue(costs, 'annual.administrativeOverheadPercentage.complexityModifier', 0).toFixed(1)}% +
                              State (${state || 'N/A'}): ${getValue(costs, 'annual.administrativeOverheadPercentage.stateModifier', 0).toFixed(1)}% +
                              Subsector: ${getValue(costs, 'annual.administrativeOverheadPercentage.subsectorModifier', 0).toFixed(1)}%
                              (Capped between 15-25%)`}
                         </div>
                     </div>
                 )}
                  {isFiscalSponsorship && (
                      <div className="info-tooltip-inline">
                         <FaInfoCircle className="info-icon-inline" />
                         <div className="tooltip-content-inline">
                            Standard administrative fee covering all operational infrastructure provided by the sponsor.
                         </div>
                     </div>
                  )}
             </div>

            {/* Cost Breakdown Section - Uses displayItems array */}
            <div className="cost-breakdown">
                <div className="section-header">
                    <FaRegFileAlt className="section-icon" />
                    <h3>{isFiscalSponsorship ? 'Fee Detail' : 'Cost Breakdown'}</h3>
                </div>
                <div className="costs-list">
                    {displayItems.map((item) => {
                        // Skip items with no value unless it's a header or special row
                         if (item.value === null && !item.isHeader && !item.isTotal) return null;
                         // Optionally hide $0 value items unless it's a total/header
                         // if (item.value === 0 && !item.isTotal && !item.isHeader) return null;

                         if (item.isHeader) {
                              return (
                                 <div key={item.key} className="cost-item header-row">
                                     <span className="cost-label header-label">{item.label}</span>
                                 </div>
                              );
                         }

                        return (
                            <div key={item.key} className={`cost-item ${item.isTotal ? 'total-row' : ''} ${item.isBold ? 'bold-row' : ''}`}>
                                <div className="cost-label-group">
                                    <span className="cost-label">{item.label}</span>
                                    {item.tooltip && (
                                        <div className="info-tooltip">
                                            <FaInfoCircle className="info-icon" />
                                            <div className="tooltip-content">{item.tooltip}</div>
                                        </div>
                                    )}
                                </div>
                                <span className="cost-value">
                                     {formatCurrencyDisplay(item.value)}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>

            {/* Conditional Sections */}
            {isFiscalSponsorship ? (
                 <div className="whats-included">
                    <div className="section-header"><FaShieldAlt className="section-icon" /><h3>What's Included</h3></div>
                     <div className="benefits-grid">
                          <div className="benefits-section"><h4>Core Benefits</h4><ul className="features-list">{coreBenefitsFS.map((b,i)=><li key={i}><FaCheckCircle className="included-icon green"/>{b}</li>)}</ul></div>
                          <div className="benefits-section"><h4>Additional Support</h4><ul className="features-list">{additionalServicesFS.map((s,i)=><li key={i}><FaCheckCircle className="included-icon green"/>{s}</li>)}</ul></div>
                     </div>
                 </div>
            ) : (
                <div className="independent-details">
                    {/* State Specific Requirements Section */}
                     {state && stateRequirementsFlags && (
                        <div className="state-specific-requirements-section">
                           <div className="section-header"><FaBuilding className="section-icon" /><h3>State Compliance Overview ({state})</h3></div>
                           {renderStateRequirements()}
                        </div>
                     )}
                
                </div>
            )}
        </div> // End comparison-card
    );
};

export default CostBreakdown;